import React, { Fragment, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { Box, Typography, Card, CircularProgress } from "@material-ui/core"
import CheckIcon from "@material-ui/icons/Check"
import {
  toggleSendBillingDialog,
  toggleDigitalSignatureDialog,
  toggleBillingPaymentDialog,
} from "../../../../actions/billingDialogs"
import { updateClientEstimateInvoiceStatus } from "../../../../actions/billing-crew-client"
import { openSnackBar } from "../../../../actions/snack-bar"
import formatNumberWithCommas from "../../../../utils/formatNumberWithCommas"
import calculateTotals from "../../../../utils/calculateTotals"
import moment from "moment"
import classNames from "classnames"
import PropTypes from "prop-types"

import GreenButton from "../../Common/GreenButton/index"
import LoadingButton from "../../../LoadingButton/index"

import useStyles from "./styles"
import SendLog from "../SendLog/index"
import convertAdeskToOrganization from "../../../../utils/convertAdeskToOrganization"

const ClientInvoiceRightSide = ({
  generalData = {},
  setGeneralData = () => { },
  usersRolesArray,
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [acceptWorking, toggleAcceptWorking] = useState(false)
  const [reportWorking, toggleReportWorking] = useState(false)

  const companyLogo =
    generalData &&
    generalData.company_logo_image &&
    JSON.parse(generalData.company_logo_image).url

  const discount = (generalData && generalData.discount) || 0
  const invoiceAccepted = generalData && Boolean(generalData.invoice_accepted)
  const depositRequired = generalData && Boolean(generalData.deposit_required)

  const changePaymentMethodIfCheck =
    (!generalData.deposit_required &&
      (generalData.first_payment_type === "check" ||
        generalData.first_payment_type === "ach")) ||
    (generalData.deposit_required &&
      (generalData.second_payment_type === "check" ||
        generalData.second_payment_type === "ach"))

  const paidAmt = useMemo(
    () => {
      let totalPaid
      let depositRequired = Boolean(generalData.deposit_required)
      //if depositRequired
      let firstPaymentPaid = Boolean(generalData.first_paid)
      let secondPaymentPaid = Boolean(generalData.second_paid)
      //if not depositRequired
      let fullPaymentPaid = Boolean(Boolean(generalData.full_paid))

      if (depositRequired) {
        if (firstPaymentPaid) {
          if (secondPaymentPaid) {
            totalPaid =
              generalData.first_half_amount + generalData.second_half_amount
          } else {
            totalPaid = generalData.first_half_amount
          }
        } else {
          if (secondPaymentPaid) {
            totalPaid = generalData.second_half_amount
          } else {
            totalPaid = 0
          }
        }
      } else {
        if (fullPaymentPaid) {
          totalPaid = generalData.full_amount
        } else {
          totalPaid = 0
        }
      }
      return totalPaid
    },
    [generalData]
  )



  const sentFinalPayment = depositRequired
    ? generalData.second_paid === 1
    : generalData.full_paid === 1

  const finalPaymentAccepted = depositRequired
    ? Boolean(generalData.second_paid)
    : Boolean(generalData.full_paid)

  const amounts = useMemo(
    () => {
      if (usersRolesArray) {
        return calculateTotals(usersRolesArray, paidAmt, discount)
      }
    },
    [usersRolesArray, paidAmt, discount]
  )


  // *** APPROVE INVOICE  ***

  const handleApproveInvoice = dialogVals => {
    toggleAcceptWorking(true)
    let actionData = {
      url_type: "accepted",
      type: "invoice",
      client_invoice_signature: dialogVals.fullName,
      client_invoice_terms_accepted: Number(dialogVals.termsAccepted),
    }
    dispatch(
      updateClientEstimateInvoiceStatus(actionData, generalData.shoot_id)
    )
      .then(() => {
        toggleAcceptWorking(false)
        setGeneralData(oldState => {
          return {
            ...oldState,
            invoice_accepted: 1,
            client_invoice_signature: actionData.client_invoice_signature,
            client_invoice_terms_accepted: 1,
            invoice_accepted_date_time: moment().format(),
          }
        })
        dispatch(openSnackBar({ message: "You have approved this invoice" }))
      })
      .catch(err => {
        console.log(err)
        toggleAcceptWorking(false)
        dispatch(openSnackBar({ message: "Error approving invoice" }))
      })
  }

  const openSignatureDialog = () => {
    let dialogData = {
      headerLogo: companyLogo,
      acceptFunc: handleApproveInvoice,
    }
    dispatch(toggleDigitalSignatureDialog(true, dialogData))
  }
  // *** END APPROVE INVOICE  ***

  // *** REPORT INVOICE  ***
  const handleReportInvoiceIssue = dialogVals => {
    toggleReportWorking(true)
    const request = {
      url_type: "report",
      email_to: dialogVals.recipients.join(", "),
      subject: dialogVals.subject,
      message: dialogVals.message,
    }
    dispatch(updateClientEstimateInvoiceStatus(request, generalData.shoot_id))
      .then(() => {
        toggleReportWorking(false)
        dispatch(
          openSnackBar({
            message: `Reported issue details to ${generalData.company_name
              } staff`,
          })
        )
      })
      .catch(err => {
        console.log(err)
        toggleReportWorking(false)
        dispatch(openSnackBar({ message: "Error sending issue details" }))
      })
  }

  const openReportIssue = () => {
    let defaultReportEmail =
      generalData.representative_email || generalData.creator_email
        ? (generalData.representative_email || generalData.creator_email)
          .split(",")
          .map(x => x.trim())
        : null
    let dialogData = {
      type: "Issue",
      shootName: generalData.shoot_name,
      defaultEmail: defaultReportEmail,
      sendFunc: handleReportInvoiceIssue,
    }
    dispatch(toggleSendBillingDialog(true, dialogData))
  }

  const openPaymentDialog = () => {
    const data = {
      final: true,
      amountDue: amounts.remaining,
      totalAmount: amounts.original + amounts.additional - discount,
      headerLogo: companyLogo,
      generalData: generalData,
      setGeneralData: setGeneralData,
    }
    dispatch(toggleBillingPaymentDialog(true, data))
  }


  return (
    <Box display="flex" flexDirection="column">
      <SendLog data={generalData} />
      <Box alignSelf="flex-start" mt={3} mb={2}>
        <Typography
          style={{
            fontSize: "16px",
            fontWeight: 600,
            textAlign: "left",
          }}
        >
          Final Invoice
        </Typography>
      </Box>
      {!invoiceAccepted ? (
        <Fragment>
          <Box className={classes.card}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              textAlign="left"
            >

              <Typography className={classes.grayFont}>
                Please review your final invoice from {convertAdeskToOrganization(generalData.crewmama_or_gtt)}.
                If you have any questions on items, please flag and submit your
                questions back to the {convertAdeskToOrganization(generalData.crewmama_or_gtt)} team
              </Typography>
            </Box>
          </Box>
          <Box alignSelf="center" width="80%" mt={3}>
            <GreenButton
              fullWidth
              variant="contained"
              onClick={openSignatureDialog}
              disabled={acceptWorking}
            >
              {acceptWorking ? (
                <CircularProgress
                  size={24}
                  style={{ color: "#fff" }}
                  disabled={acceptWorking}
                />
              ) : (
                "Accept Invoice"
              )}
            </GreenButton>
          </Box>
        </Fragment>
      ) : (
        <Box mb={1}>
          {/* Digital Sigantrue Card */}
          <Card classes={{ root: classes.card }}>
            <Box className={classes.rowWrapper}>
              <Typography variant="h3">Digital Signature</Typography>
              <Box
                className={classNames(classes.statusDot, {
                  [classes.acceptedDot]: invoiceAccepted,
                })}
              >
                <CheckIcon className={classes.checkIcon} />
              </Box>
            </Box>
            <Typography
              align="left"
              variant="subtitle1"
              style={{
                marginBottom: 10,
                marginTop: 10,
              }}
            >
              Signed on{" "}
              {moment(generalData.invoice_accepted_date_time).format(
                "M/D/YY[ @ ]h:mm A"
              )}{" "}
              by {generalData.client_invoice_signature}
            </Typography>
          </Card>
          {/* Status Card */}
          <Card classes={{ root: classes.card }} style={{ marginTop: "20px" }}>
            <Box className={classes.rowWrapper}>
              <Typography variant="h3">Final Payment Sent</Typography>
              <Box
                className={classNames(classes.statusDot, {
                  [classes.acceptedDot]: sentFinalPayment,
                })}
              >
                {sentFinalPayment && (
                  <CheckIcon className={classes.checkIcon} />
                )}
              </Box>
            </Box>
            <Box mt={2}>
              <Box className={classes.rowWrapper}>
                <Typography className={classes.greenFont}>
                  Balance Due:
                </Typography>
                <Typography className={classes.greenFont}>
                  $
                  {amounts && formatNumberWithCommas(amounts.remaining)}
                </Typography>
              </Box>
              {(!finalPaymentAccepted || amounts.remaining > 0) && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <GreenButton variant="outlined" onClick={openPaymentDialog}>
                    {changePaymentMethodIfCheck
                      ? "Change Payment Method"
                      : "Submit Payment"}
                  </GreenButton>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      )}
      <Box alignSelf="center" width="80%" mt={2}>
        <LoadingButton
          fullWidth
          variant="outlined"
          className={classes.redButton}
          onClick={openReportIssue}
          disabled={reportWorking}
        >
          {reportWorking ? (
            <CircularProgress
              size={20}
              color="inherit"
              style={{ margin: "4px 0" }}
            />
          ) : (
            "Report An Issue"
          )}
        </LoadingButton>
      </Box>
    </Box>
  )
}

ClientInvoiceRightSide.propTypes = {
  generalData: PropTypes.object,
  setGeneralData: PropTypes.func,
  usersRolesArray: PropTypes.array,
}

export default ClientInvoiceRightSide
